import type { FC, HTMLAttributes, ReactNode } from 'react'
import { useRef, useState } from 'react'
import { TreeViewContext, useTreeViewContext } from './tree-view-context.js'
import styles from './tree-view.module.css'

const getVisibleItems = (root: HTMLUListElement): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>('[role="treeitem"] > div[data-tree-item-id]'))

export interface SimpleTreeViewProps extends HTMLAttributes<HTMLUListElement> {
  expandedItems?: string[]
  selectedItems?: string | string[]
  onItemExpansionToggle?: (ev: unknown, itemId: string) => void
  onItemSelectionToggle?: (ev: unknown, itemId: string) => void
  onSelectedItemsChange?: (ev: unknown, itemId: string | null) => void
  slots?: {
    collapseIcon?: FC
    expandIcon?: FC
  }
  children?: ReactNode
}

export const SimpleTreeView: FC<SimpleTreeViewProps> = ({
  expandedItems = [],
  selectedItems = '',
  onItemExpansionToggle,
  onItemSelectionToggle,
  onSelectedItemsChange,
  slots,
  children,
  ...restProps
}) => {
  const [focusedItemId, setFocusedItemId] = useState<string | null>(null)
  const treeRootRef = useRef<HTMLUListElement>(null)

  const handleFocus = (ev: React.FocusEvent) => {
    const target = ev.target as HTMLElement
    const itemId = target.dataset?.treeItemId
    if (itemId) {
      setFocusedItemId(itemId)
      return
    }
    // If the tree root itself received focus (initial tab-in), focus the selected or first item
    if (target === treeRootRef.current) {
      const root = treeRootRef.current
      if (!root) {
        return
      }
      const items = getVisibleItems(root)
      // Try to focus the selected item first
      const selectedId = Array.isArray(selectedItems) ? selectedItems[0] : selectedItems
      const selectedEl = selectedId ? items.find(el => el.dataset.treeItemId === selectedId) : null
      const targetEl = selectedEl ?? items[0]
      if (targetEl) {
        targetEl.focus()
      }
    }
  }

  return (
    <TreeViewContext.Provider
      value={{
        expandedItems,
        selectedItems,
        focusedItemId,
        treeRootRef,
        onItemExpansionToggle,
        onItemSelectionToggle,
        onSelectedItemsChange,
        CollapseIcon: slots?.collapseIcon,
        ExpandIcon: slots?.expandIcon,
      }}
    >
      <ul
        ref={treeRootRef}
        role="tree"
        tabIndex={focusedItemId === null ? 0 : -1}
        onFocus={handleFocus}
        className={styles.tree}
        {...restProps}
      >
        {children}
      </ul>
    </TreeViewContext.Provider>
  )
}

export interface TreeItemProps extends Omit<HTMLAttributes<HTMLLIElement>, 'id'> {
  itemId: string
  label?: ReactNode
  children?: ReactNode
}

export const TreeItem: FC<TreeItemProps> = ({ itemId, label, children, ...restProps }) => {
  const {
    expandedItems,
    selectedItems,
    focusedItemId,
    treeRootRef,
    onItemExpansionToggle,
    onItemSelectionToggle,
    onSelectedItemsChange,
    CollapseIcon,
    ExpandIcon,
  } = useTreeViewContext()

  const hasChildren = children != null && children !== false
  const isExpanded = expandedItems.includes(itemId)
  const isSelected = Array.isArray(selectedItems) ? selectedItems.includes(itemId) : selectedItems === itemId
  const isFocused = focusedItemId === itemId
  const isHighlighted = isSelected || isFocused

  const handleToggleExpand = (ev: React.MouseEvent) => {
    ev.stopPropagation()
    onItemExpansionToggle?.(ev, itemId)
  }

  const handleSelect = (ev: React.MouseEvent | React.KeyboardEvent) => {
    onItemSelectionToggle?.(ev, itemId)
    onSelectedItemsChange?.(ev, itemId)
  }

  const handleClick = (ev: React.MouseEvent) => {
    // Ensure the focusable div receives focus when clicking on label content
    ;(ev.currentTarget as HTMLElement).focus()
    handleSelect(ev)
  }

  const handleKeyDown = (ev: React.KeyboardEvent) => {
    const root = treeRootRef.current
    if (!root) {
      return
    }

    const items = getVisibleItems(root)
    const currentIndex = items.findIndex(el => el.dataset.treeItemId === itemId)

    switch (ev.key) {
      case 'ArrowDown': {
        ev.preventDefault()
        const next = items[currentIndex + 1]
        next?.focus()
        break
      }
      case 'ArrowUp': {
        ev.preventDefault()
        const prev = items[currentIndex - 1]
        prev?.focus()
        break
      }
      case 'ArrowRight': {
        ev.preventDefault()
        if (hasChildren && !isExpanded) {
          onItemExpansionToggle?.(ev, itemId)
        } else if (hasChildren && isExpanded) {
          // Focus first child (next visible item)
          const next = items[currentIndex + 1]
          next?.focus()
        }
        break
      }
      case 'ArrowLeft': {
        ev.preventDefault()
        if (hasChildren && isExpanded) {
          onItemExpansionToggle?.(ev, itemId)
        } else {
          // Move focus to parent item
          const parentLi = ev.currentTarget
            .closest('li[role="treeitem"]')
            ?.parentElement?.closest('li[role="treeitem"]')
          const parentDiv = parentLi?.querySelector<HTMLElement>(':scope > div[data-tree-item-id]')
          parentDiv?.focus()
        }
        break
      }
      case 'Home': {
        ev.preventDefault()
        items[0]?.focus()
        break
      }
      case 'End': {
        ev.preventDefault()
        items[items.length - 1]?.focus()
        break
      }
      case 'Enter':
      case ' ': {
        ev.preventDefault()
        handleSelect(ev)
        break
      }
      default: {
        // Type-ahead: single printable character
        if (ev.key.length === 1 && !ev.ctrlKey && !ev.metaKey && !ev.altKey) {
          ev.preventDefault()
          const char = ev.key.toLowerCase()
          // Search from current index + 1, wrapping around
          for (let i = 1; i <= items.length; i++) {
            const idx = (currentIndex + i) % items.length
            const text = items[idx]?.textContent?.trim().toLowerCase()
            if (text?.startsWith(char)) {
              items[idx]?.focus()
              break
            }
          }
        }
      }
    }
  }

  const Icon = isExpanded ? CollapseIcon : ExpandIcon

  return (
    <li role="treeitem" aria-expanded={hasChildren ? isExpanded : undefined} aria-selected={isSelected} {...restProps}>
      <div
        data-tree-item-id={itemId}
        tabIndex={focusedItemId === itemId ? 0 : -1}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={`${styles.item} ${isHighlighted ? styles.itemHighlighted : ''}`}
      >
        <span
          onClick={hasChildren ? handleToggleExpand : undefined}
          className={`${styles.expandIcon} ${hasChildren ? '' : styles.expandIconHidden}`}
        >
          {Icon ? <Icon /> : isExpanded ? '▾' : '▸'}
        </span>
        {label}
      </div>
      {hasChildren && isExpanded ? (
        <ul role="group" className={styles.group}>
          {children}
        </ul>
      ) : null}
    </li>
  )
}
